import { Typography } from "@mui/material";
import { AgGridReact, AgGridReactProps } from "ag-grid-react";
import { forwardRef, ReactNode, useCallback, useMemo } from "react";
import AG_GRID_LOCALE_TH from "../../locale.th";
import CustomDate from "./CustomDate";

import "ag-grid-enterprise";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-material.css";
import "../../table.css";

interface Props {
  isClientSide?: boolean;
  autoHeight?: boolean;
  suppressMenu?: boolean;
  disabledSidebar?: boolean;
  disableFloatingFilter?: boolean;
  headerClass?: [string];
  disableResized?: boolean;
  paginationSize?: number;
  height?: number | string;
  width?: number | string;
  children?: ReactNode;
  path?: string;
  disableMinHeight?: boolean;
  suppressRowClickSelection?: boolean;
}

type ExtendProps = AgGridReactProps & Props;

const AgGrid = forwardRef<any, ExtendProps>(
  (
    {
      isClientSide,
      rowData,
      columnDefs,
      onRowDoubleClicked,
      height,
      width,
      groupDisplayType,
      groupDefaultExpanded,
      groupIncludeFooter,
      groupIncludeTotalFooter,
      // searchBox,
      onGridReady,
      onFirstDataRendered,
      masterDetail,
      detailCellRenderer,
      detailRowHeight,
      autoHeight,
      rowSelection,
      onRowClicked,
      onRowSelected,
      enableRangeSelection,
      components,
      onRowDataUpdated,
      suppressMenu,
      rowDragManaged,
      onRowDragMove,
      disabledSidebar,
      disableFloatingFilter,
      rowDragEntireRow,
      getRowId,
      headerClass,
      rowDragMultiRow,
      rowMultiSelectWithClick,
      disableResized,
      onFilterChanged,
      detailCellRendererParams,
      autoGroupColumnDef,
      paginationSize,
      path,
      suppressContextMenu,
      disableMinHeight,
      suppressRowClickSelection,
      onSelectionChanged,
    },
    ref,
  ) => {
    const sideBar = {
      toolPanels: ["columns", "filters"],
    };

    const defaultColDef = useMemo(() => {
      return {
        editable: false,
        floatingFilter:
          typeof disableFloatingFilter !== "undefined"
            ? !disableFloatingFilter
            : isClientSide
              ? false
              : true,
        filter: isClientSide ? false : true,
        resizable: disableResized ? false : true,
        sortable: isClientSide ? false : true,
        suppressMenu: suppressMenu ? true : false,
        headerClass: headerClass || [""],
      };
    }, [
      disableFloatingFilter,
      disableResized,
      headerClass,
      isClientSide,
      suppressMenu,
    ]);

    const localeText = useMemo(() => {
      return AG_GRID_LOCALE_TH;
    }, []);

    // const onFilterTextBoxChanged = useCallback(
    //   (event: any) => {
    //     ref.current.api.setQuickFilter(
    //       document.getElementById("filter-text-box").value
    //     );
    //   },
    //   [ref]
    // );

    const CustomizedNoRowsOverlay = ({ noRowsMessageFunc }: any) => (
      <Typography>{noRowsMessageFunc()}</Typography>
    );

    const noRowsOverlayComponent = useMemo(() => {
      return CustomizedNoRowsOverlay;
    }, []);

    const noRowsOverlayComponentParams = useMemo(() => {
      return {
        noRowsMessageFunc: () => "ไม่มีข้อมูล",
      };
    }, []);

    const getContextMenuItems = useCallback(
      (params: any) => {
        return [
          // ...params.defaultItems,
          // {
          //   name: "Export Selected Rows",
          //   icon: '<span class="ag-icon ag-icon-save" unselectable="on" role="presentation"></span>',
          //   subMenu: [
          //     {
          //       name: "CSV Export",
          //       icon: '<span class="ag-icon ag-icon-csv" unselectable="on" role="presentation"></span>',
          //       action: () =>
          //         params.api.exportDataAsCsv({
          //           onlySelected: true,
          //         }),
          //     },
          //     {
          //       name: "Excel Export",
          //       icon: '<span class="ag-icon ag-icon-excel" unselectable="on" role="presentation"></span>',
          //       action: () =>
          //         params.api.exportDataAsExcel({
          //           onlySelected: true,
          //         }),
          //     },
          //   ],
          // },
          {
            name: "Open Document in New Tab",
            icon: '<span class="material-icons"  style={{fontSize: "18px"}}>open_in_new</span>',
            action: () => {
              if (params.node != null) {
                let documentPath;
                const uniqueId = params.node.data.id ?? "";

                if (path === "/inventory/items") {
                  const { item_sku } = params.node.data;
                  const { item } = item_sku;
                  const { id } = item;
                  documentPath = `/inventory/items/${encodeURIComponent(
                    id,
                  )}?tab=item&subtab=general`;
                } else {
                  documentPath = `${path}/${encodeURIComponent(uniqueId)}`;
                }

                window.open(documentPath, "_blank");
              }
            },
          },
        ];
      },
      [path],
    );

    const defaultComponents = useMemo(() => {
      return {
        agDateInput: CustomDate,
      };
    }, []);

    return (
      <div
        id="myGrid"
        className="ag-theme-material"
        style={{
          height: height || "100%",
          // width: "calc(100vw - 300px)",
          width: width || "100%",
          minHeight: !disableMinHeight ? "450px" : undefined,
          // maxWidth: ,
          position: "relative",
        }}
      >
        <AgGridReact
          domLayout={autoHeight ? "autoHeight" : "normal"}
          ref={ref}
          onGridReady={onGridReady}
          onFirstDataRendered={onFirstDataRendered}
          rowData={rowData}
          localeText={localeText}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          sideBar={disabledSidebar ? false : sideBar}
          detailRowHeight={detailRowHeight}
          enableRangeSelection={enableRangeSelection}
          noRowsOverlayComponent={noRowsOverlayComponent}
          noRowsOverlayComponentParams={noRowsOverlayComponentParams}
          onRowDoubleClicked={onRowDoubleClicked}
          onRowClicked={onRowClicked}
          onRowSelected={onRowSelected}
          groupDisplayType={groupDisplayType}
          groupDefaultExpanded={groupDefaultExpanded}
          groupIncludeFooter={groupIncludeFooter}
          groupIncludeTotalFooter={groupIncludeTotalFooter}
          detailCellRenderer={detailCellRenderer}
          masterDetail={masterDetail}
          detailCellRendererParams={detailCellRendererParams}
          rowModelType={isClientSide || rowData ? "clientSide" : "serverSide"}
          // serverSideStoreType={isClientSide || rowData ? "full" : "partial"}
          pagination={isClientSide && rowData ? false : true}
          paginationPageSize={
            paginationSize
              ? paginationSize
              : isClientSide && rowData
                ? undefined
                : 100
          }
          cacheBlockSize={
            isClientSide && rowData ? undefined : paginationSize || 100
          }
          rowSelection={rowSelection}
          getContextMenuItems={getContextMenuItems}
          components={components || defaultComponents}
          onRowDataUpdated={onRowDataUpdated}
          rowDragMultiRow={rowDragMultiRow}
          rowDragManaged={rowDragManaged}
          onRowDragMove={onRowDragMove}
          rowDragEntireRow={rowDragEntireRow}
          getRowId={getRowId}
          enableCellTextSelection={true}
          suppressContextMenu={suppressContextMenu || !path}
          suppressMovableColumns={isClientSide ? true : false}
          suppressAggFuncInHeader={true}
          rowMultiSelectWithClick={rowMultiSelectWithClick}
          onFilterChanged={onFilterChanged}
          autoGroupColumnDef={autoGroupColumnDef}
          // serverSideInfiniteScroll={isClientSide ? false : true}
          serverSideSortOnServer={isClientSide ? false : true}
          serverSideSortAllLevels={isClientSide ? false : true}
          serverSideFilterOnServer={isClientSide ? false : true}
          suppressRowClickSelection={suppressRowClickSelection ? true : false}
          getRowStyle={(params: any) => {
            if (params.node.footer) {
              return { fontWeight: "bold" };
            }
          }}
          onSelectionChanged={onSelectionChanged}
        />
      </div>
    );
  },
);

export default AgGrid;
